import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Info } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { registerUser } from '@/lib/api';

const FieldRow = ({ children }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{children}</div>
);

const Field = ({ label, children, hint }) => (
  <label className="block">
    <div className="font-mono text-[10px] uppercase tracking-widest text-muted-sage mb-1">{label}</div>
    {children}
    {hint ? <div className="mt-1 text-[11px] text-muted-sage">{hint}</div> : null}
  </label>
);

const inputCls = 'w-full h-12 rounded-xl bg-surface border border-pd px-4 text-sm focus:border-[color:var(--pd-accent-yellow)] focus:outline-none';

const RegisterPage = () => {
  const [search] = useSearchParams();
  const returnTo = search.get('returnTo') || '/account';
  const nav = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    full_name: '', email: '', password: '', date_of_birth: '',
    phone: '', address_line1: '', address_line2: '', city: '', postcode: '',
    marketing_opt_in: false, accept_terms: false,
    // Stage 3: mandatory active choice — either a £ amount or explicit no-cap
    spend_limit_gbp: '', no_spend_limit_acknowledged: false,
  });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.accept_terms) { toast.error('Please accept the Terms & Privacy Policy to continue.'); return; }
    // Stage 3 gate — must have chosen a limit OR acknowledged no cap
    const hasNumericLimit = form.spend_limit_gbp !== '' && form.spend_limit_gbp !== null && !isNaN(Number(form.spend_limit_gbp));
    if (!hasNumericLimit && !form.no_spend_limit_acknowledged) {
      toast.error('Please set a monthly spend limit or explicitly choose no limit.');
      return;
    }
    setLoading(true);
    try {
      const { accept_terms: _ignore, ...rest } = form;
      const payload = {
        ...rest,
        spend_limit_gbp: hasNumericLimit ? Number(form.spend_limit_gbp) : null,
        no_spend_limit_acknowledged: !hasNumericLimit && form.no_spend_limit_acknowledged,
      };
      const r = await registerUser(payload);
      localStorage.setItem('pd_user_token', r.token);
      localStorage.setItem('pd_user', JSON.stringify(r.user));
      toast.success('Account created — welcome to The Padel Draw.');
      nav(returnTo, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not create your account.');
    } finally { setLoading(false); }
  };

  return (
    <div className="container-pd py-16 max-w-2xl" data-testid="register-page">
      <div className="font-mono text-[10px] text-[color:var(--pd-gold)] uppercase tracking-[0.2em]">Create account</div>
      <h1 className="font-display text-5xl md:text-6xl leading-none mt-3">Open your account.</h1>
      <p className="mt-4 text-muted-sage">
        Accounts are required for all entries — paid and free postal alike. This lets us protect players (spend limits, pause options, one-account-per-person) as we adopt the DCMS Prize Draws &amp; Competitions Code in full.
      </p>

      <form onSubmit={submit} className="mt-8 space-y-4" data-testid="register-form">
        <Field label="Full name">
          <input required type="text" value={form.full_name} onChange={(e)=>update('full_name', e.target.value)} className={inputCls} data-testid="register-fullname" />
        </Field>

        <FieldRow>
          <Field label="Email">
            <input required type="email" value={form.email} onChange={(e)=>update('email', e.target.value)} className={inputCls} data-testid="register-email" />
          </Field>
          <Field label="Password" hint="Minimum 8 characters.">
            <input required type="password" minLength={8} value={form.password} onChange={(e)=>update('password', e.target.value)} className={inputCls} data-testid="register-password" />
          </Field>
        </FieldRow>

        <FieldRow>
          <Field label="Date of birth" hint="You must be 18 or over to enter competitions.">
            <input required type="date" value={form.date_of_birth} onChange={(e)=>update('date_of_birth', e.target.value)} className={inputCls} data-testid="register-dob" />
          </Field>
          <Field label="Phone number">
            <input required type="tel" value={form.phone} onChange={(e)=>update('phone', e.target.value)} className={inputCls} data-testid="register-phone" />
          </Field>
        </FieldRow>

        <Field label="Address line 1">
          <input required type="text" value={form.address_line1} onChange={(e)=>update('address_line1', e.target.value)} className={inputCls} data-testid="register-address1" />
        </Field>
        <Field label="Address line 2 (optional)">
          <input type="text" value={form.address_line2} onChange={(e)=>update('address_line2', e.target.value)} className={inputCls} data-testid="register-address2" />
        </Field>
        <FieldRow>
          <Field label="Town / City">
            <input required type="text" value={form.city} onChange={(e)=>update('city', e.target.value)} className={inputCls} data-testid="register-city" />
          </Field>
          <Field label="Postcode">
            <input required type="text" value={form.postcode} onChange={(e)=>update('postcode', e.target.value)} className={inputCls} data-testid="register-postcode" />
          </Field>
        </FieldRow>

        {/* Stage 3 — mandatory spend-limit choice (Code clause 1.4).
            Copy is intentionally condensed: the £250 cap, 24h raise delay,
            instant-lower, and £0 block are ALL still enforced server-side
            and via the same fields — this block just presents them without
            walls of body text. Full detail is one tap away via the "i"
            popover and on the Safer Play page. */}
        <div className="mt-2 rounded-2xl border border-[color:var(--pd-gold)]/40 bg-surface p-5" data-testid="register-spend-limit-block">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--pd-gold)]">Player protection</div>
          <div className="mt-1 flex items-center gap-2">
            <h2 className="font-display text-2xl">Monthly spend limit</h2>
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  aria-label="More about the monthly spend limit"
                  className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-[color:var(--pd-gold)]/40 text-[color:var(--pd-gold)] hover:bg-[color:var(--pd-gold)]/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--pd-gold)]/60"
                  data-testid="register-spend-limit-info"
                >
                  <Info size={13} />
                </button>
              </PopoverTrigger>
              <PopoverContent side="top" align="start" className="w-80 text-sm bg-surface border-[color:var(--pd-gold)]/40">
                <div className="space-y-2 text-muted-sage">
                  <p><span className="text-[color:var(--pd-text)]">£250/month platform cap</span> applies to every account, regardless of card type.</p>
                  <p>Lowering your limit is <span className="text-[color:var(--pd-text)]">instant</span>. Raising it waits <span className="text-[color:var(--pd-text)]">24 hours</span>.</p>
                  <p>Set to <span className="text-[color:var(--pd-text)]">£0</span> to block all purchases. Change any time from your dashboard or <Link to="/safer-play" className="text-[color:var(--pd-accent-yellow)] underline">Safer Play</Link>.</p>
                </div>
              </PopoverContent>
            </Popover>
          </div>
          <p className="text-sm text-muted-sage mt-1.5">
            Set a personal cap below our standard £250/month limit, or skip for now.
          </p>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-end">
            <Field label="My monthly limit (£, max 250)" hint="Set to £0 to block all purchases until you raise it.">
              <input
                type="number"
                min={0}
                max={250}
                step={5}
                value={form.spend_limit_gbp}
                onChange={(e) => { update('spend_limit_gbp', e.target.value); if (e.target.value !== '') update('no_spend_limit_acknowledged', false); }}
                disabled={form.no_spend_limit_acknowledged}
                placeholder="e.g. 50"
                className={`${inputCls} ${form.no_spend_limit_acknowledged ? 'opacity-50 cursor-not-allowed' : ''}`}
                data-testid="register-spend-limit"
              />
            </Field>
          </div>
          <label className="mt-4 flex items-start gap-2 text-sm text-muted-sage">
            <input
              type="checkbox"
              checked={form.no_spend_limit_acknowledged}
              onChange={(e) => { update('no_spend_limit_acknowledged', e.target.checked); if (e.target.checked) update('spend_limit_gbp', ''); }}
              className="mt-1 accent-[color:var(--pd-accent-yellow)]"
              data-testid="register-no-spend-limit"
            />
            <span>
              <span className="text-[color:var(--pd-text)]">I&rsquo;m happy with the standard £250 limit &mdash; skip for now.</span>
              <span className="block text-[11px] mt-0.5">You can add a lower personal limit anytime from your account dashboard.</span>
            </span>
          </label>
        </div>

        <label className="flex items-start gap-2 text-sm text-muted-sage">
          <input type="checkbox" checked={form.marketing_opt_in} onChange={(e)=>update('marketing_opt_in', e.target.checked)} className="mt-1 accent-[color:var(--pd-accent-yellow)]" data-testid="register-marketing" />
          <span>Email me when new competitions launch. Unsubscribe anytime.</span>
        </label>

        <label className="flex items-start gap-2 text-sm text-muted-sage">
          <input required type="checkbox" checked={form.accept_terms} onChange={(e)=>update('accept_terms', e.target.checked)} className="mt-1 accent-[color:var(--pd-accent-yellow)]" data-testid="register-accept-terms" />
          <span>
            I confirm I am 18 or over and I accept the{' '}
            <Link to="/terms" className="text-[color:var(--pd-accent-yellow)] underline">Terms &amp; Conditions</Link>{' '}and{' '}
            <Link to="/privacy" className="text-[color:var(--pd-accent-yellow)] underline">Privacy Policy</Link>.
          </span>
        </label>

        <button disabled={loading} className="w-full bg-[color:var(--pd-accent-yellow)] text-[#14251c] rounded-xl px-4 py-3.5 font-medium disabled:opacity-60" data-testid="register-submit">
          {loading ? 'Creating account…' : 'Create account'}
        </button>

        <p className="text-xs text-muted-sage text-center">
          Already have an account?{' '}
          <Link to={`/login?returnTo=${encodeURIComponent(returnTo)}`} className="text-[color:var(--pd-accent-yellow)]">Sign in</Link>
        </p>
      </form>
    </div>
  );
};

export default RegisterPage;
